import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  useTheme,
  alpha,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  ArrowForward as NextIcon,
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { useToast } from '../../context/ToastContext';
import config from '../../utils/config';
import ConfigurationEditor from '../../components/workflow-v2/ConfigurationEditor';
import PromptEditDialog from '../../components/workflow-v2/PromptEditDialog';
import { useUpstreamModuleCheck } from '../../hooks/useUpstreamModuleCheck';

interface ConfigData {
  categories: any[];
  version: number;
  generated_method: string;
  source_module1_topics: number;
  source_module3_categories: number;
  total_categories: number;
  keyword_stats?: {
    total_keywords?: number;
  };
}

const BatchConfigStep: React.FC = () => {
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  
  // default to guardrail mode
  const batchMode = searchParams.get('snowflake') === '1' ? 'snowflake' : 'guardrail';
  
  const [configData, setConfigData] = useState<ConfigData | null>(null);
  const [editedCategories, setEditedCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'weight-asc' | 'weight-desc'>('name-asc');
  
  // Check if most recent module is from upstream steps
  const { 
    mostRecentModuleIsUpstream, 
    isCheckingModules, 
    setMostRecentModuleIsUpstream 
  } = useUpstreamModuleCheck(workflowId, ['fileupload_v2', 'module1_v2', 'module3_v2']);
  
  // Prompt preview modal state
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [generatedPrompt, setGeneratedPrompt] = useState<string>('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [validationError, setValidationError] = useState<string>('');
  
  // Load config when module check completes (unless upstream module is most recent)
  useEffect(() => {
    // Wait for module check to complete
    if (isCheckingModules) {
      return;
    }
    
    // Skip loading if upstream module is most recent
    if (mostRecentModuleIsUpstream) {
      console.log('[BatchConfig] Skipping config load - upstream module is most recent');
      setIsLoading(false);
      return;
    }
    
    // Load config
    if (workflowId) {
      loadConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, isCheckingModules, mostRecentModuleIsUpstream]);
  
  // Sort categories based on current sort option
  // Always sort by type first (thematic, then intent), then apply secondary sorting
  const applySorting = useCallback((categories: any[], sortOption: 'name-asc' | 'name-desc' | 'weight-asc' | 'weight-desc') => {
    const sorted = [...categories];
    
    return sorted.sort((a, b) => {
      // Primary sort: Always by type (thematic first, then intent)
      const typeOrder = { 'thematic': 0, 'intent': 1 };
      const typeA = typeOrder[a.type as keyof typeof typeOrder] ?? 2;
      const typeB = typeOrder[b.type as keyof typeof typeOrder] ?? 2;
      
      if (typeA !== typeB) {
        return typeA - typeB;
      }
      
      // Secondary sort: Within same type, sort by selected option
      if (sortOption === 'name-asc') {
        return a.name.localeCompare(b.name);
      } else if (sortOption === 'name-desc') {
        return b.name.localeCompare(a.name);
      } else if (sortOption === 'weight-asc') {
        return (a.weight || 50) - (b.weight || 50);
      } else if (sortOption === 'weight-desc') {
        return (b.weight || 50) - (a.weight || 50);
      }
      
      return 0;
    });
  }, []);
  
  const loadConfig = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/${workflowId}`;
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.status === 'not_found') {
          showToast('No configuration found. Please complete semantic analysis first.', 'warning');
          navigate(`/workflow2/${clientId}/${workflowId}/semantic`);
          return;
        }
        
        if (data.config) {
          // Parse config if it's a string
          const parsedConfig = typeof data.config === 'string' 
            ? JSON.parse(data.config) 
            : data.config;
          setConfigData(parsedConfig);
          // Ensure all categories have a default weight
          const categoriesWithWeights = (parsedConfig.categories || []).map((cat: any) => ({
            ...cat,
            weight: cat.weight || 50 // Ensure default weight
          }));
          // Apply current sorting
          const sortedCategories = applySorting(categoriesWithWeights, sortBy);
          setEditedCategories(sortedCategories);
          setHasChanges(false);
        }
      } else {
        throw new Error('Failed to load configuration');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      showToast('Failed to load batch configuration', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [workflowId, clientId, navigate, showToast, applySorting, sortBy]);
  
  const handleReset = useCallback(async () => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }
    
    setIsLoading(true);
    try {
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/reset`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const resetConfig = data.config;
        setConfigData(resetConfig);
        // Ensure all categories have a default weight
        const categoriesWithWeights = (resetConfig.categories || []).map((cat: any) => ({
          ...cat,
          weight: cat.weight || 50 // Ensure default weight
        }));
        // Apply current sorting
        const sortedCategories = applySorting(categoriesWithWeights, sortBy);
        setEditedCategories(sortedCategories);
        setHasChanges(false);
        showToast('Configuration reset to original merge', 'success');
      } else {
        throw new Error('Failed to reset configuration');
      }
    } catch (error) {
      console.error('Error resetting config:', error);
      showToast('Failed to reset configuration', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [clientId, workflowId, showToast, applySorting, sortBy]);
  
  // Helper function to normalize categories: assign IDs and types to user-created categories
  const normalizeCategories = useCallback((categories: any[]) => {
    // First, find the highest existing thematic category number
    const existingThematicIds = categories
      .filter(c => c.category_id && c.type === 'thematic')
      .map(c => {
        // category_id is just a numeric string like "1", "2", "3"
        const numId = parseInt(c.category_id, 10);
        return isNaN(numId) ? 0 : numId;
      });
    
    // Find the highest existing intent category number
    const existingIntentIds = categories
      .filter(c => c.intent_id && c.type === 'intent')
      .map(c => {
        const numId = parseInt(c.intent_id, 10);
        return isNaN(numId) ? 0 : numId;
      });
    
    let nextThematicId = existingThematicIds.length > 0 ? Math.max(...existingThematicIds) + 1 : 1;
    let nextIntentId = existingIntentIds.length > 0 ? Math.max(...existingIntentIds) + 1 : 1;
    
    return categories.map((cat) => {
      // If category already has an ID and type, keep it as-is
      if ((cat.category_id || cat.intent_id) && cat.type) {
        // Normalize description to definition for intent categories
        const normalizedCat = cat.type === 'intent' && cat.description && !cat.definition
          ? { ...cat, definition: cat.description, description: undefined }
          : cat;
        
        // But still normalize subtopics if they exist and lack sub_ids
        // Also ensure weight is set
        if (normalizedCat.subtopics && normalizedCat.subtopics.length > 0) {
          const categoryId = normalizedCat.category_id || normalizedCat.intent_id;
          const needsNormalization = normalizedCat.subtopics.some((sub: any) => !sub.sub_id);
          
          if (needsNormalization) {
            return {
              ...normalizedCat,
              weight: normalizedCat.weight || 50, // Ensure weight is set
              subtopics: normalizedCat.subtopics.map((sub: any, subIdx: number) => {
                // If subtopic already has sub_id, keep it
                if (sub.sub_id) {
                  return sub;
                }
                // Assign sub_id based on category ID
                return {
                  ...sub,
                  sub_id: `${categoryId}.${subIdx + 1}`,
                  created_at: sub.created_at || new Date().toISOString(),
                  user_created: sub.user_created !== undefined ? sub.user_created : true,
                  source_module: sub.source_module || 'user_created'
                };
              })
            };
          }
        }
        return {
          ...normalizedCat,
          weight: normalizedCat.weight || 50 // Ensure weight is set even if no subtopic normalization needed
        };
      }
      
      // User-created intent category: assign intent_id
      if (cat.type === 'intent') {
        const currentIntentId = nextIntentId;
        nextIntentId++; // Increment for next user-created intent
        
        // Normalize description field to definition for backend compatibility
        const definition = cat.definition || cat.description || '';
        
        return {
          ...cat,
          intent_id: `${currentIntentId}`,
          type: 'intent',
          definition: definition, // Backend expects 'definition' field
          source_module: 'user_created',
          user_created: true,
          created_at: new Date().toISOString(),
          weight: cat.weight || 50,
          subtopics: [], // Intent categories don't have subtopics
          // Remove legacy description field if present
          description: undefined
        };
      }
      
      // User-created thematic category: assign category_id
      const currentId = nextThematicId;
      const newCategory = {
        ...cat,
        category_id: `${currentId}`,
        type: 'thematic',
        source_module: 'user_created',
        user_created: true,
        created_at: new Date().toISOString(),
        weight: cat.weight || 50, // Ensure default weight
        // Normalize subtopics with proper sub_ids
        subtopics: (cat.subtopics || []).map((sub: any, subIdx: number) => ({
          ...sub,
          sub_id: sub.sub_id || `${currentId}.${subIdx + 1}`,
          name: sub.name,
          created_at: sub.created_at || new Date().toISOString(),
          user_created: sub.user_created !== undefined ? sub.user_created : true,
          source_module: sub.source_module || 'user_created'
        }))
      };
      
      nextThematicId++; // Increment for next user-created category
      
      return newCategory;
    });
  }, []);
  
  // Handle category changes from the editor
  const handleCategoriesChange = useCallback((newCategories: any[]) => {
    const sorted = applySorting(newCategories, sortBy);
    setEditedCategories(sorted);
    setHasChanges(true);
    // Clear validation error when categories change
    setValidationError('');
  }, [sortBy, applySorting]);
  
  // Handle sort option change
  const handleSortChange = useCallback((newSortBy: 'name-asc' | 'name-desc' | 'weight-asc' | 'weight-desc') => {
    setSortBy(newSortBy);
    const sorted = applySorting(editedCategories, newSortBy);
    setEditedCategories(sorted);
  }, [editedCategories, applySorting]);
  
  const handleConfirmAndProceed = async () => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }
    
    // Validate that all thematic categories have at least one subtopic
    const thematicWithoutSubtopics = editedCategories.filter(
      cat => cat.type !== 'intent' && (!cat.subtopics || cat.subtopics.length === 0)
    );
    
    if (thematicWithoutSubtopics.length > 0) {
      const categoryNames = thematicWithoutSubtopics.map(cat => `"${cat.name}"`).join(', ');
      setValidationError(
        `Thematic categories require at least one subtopic: ${categoryNames}`
      );
      return;
    }
    
    // Clear any previous validation errors
    setValidationError('');
    
    setIsGeneratingPrompt(true);
    try {
      // Normalize categories: assign IDs and types to user-created categories
      const normalizedCategories = normalizeCategories(editedCategories);
      
      // Merge edited categories back into configData
      const updatedConfig = {
        ...configData,
        categories: normalizedCategories,
        total_categories: normalizedCategories.length,
        batch_mode: batchMode
      };
      
      // Call the generate-prompt endpoint
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/generate-prompt`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          config: updatedConfig
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGeneratedPrompt(data.prompt);
        setShowPromptModal(true);
      } else {
        throw new Error('Failed to generate prompt');
      }
    } catch (error) {
      console.error('Error generating prompt:', error);
      showToast('Failed to generate prompt', 'error');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };
  
  const handleFinalizePrompt = async (finalPrompt: string, model: string) => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }
    
    setIsFinalizing(true);
    try {
      // Normalize categories: assign IDs and types to user-created categories
      const normalizedCategories = normalizeCategories(editedCategories);
      
      // Merge edited categories back into configData
      const updatedConfig = {
        ...configData,
        categories: normalizedCategories,
        total_categories: normalizedCategories.length
      };
      
      // Call the finalize endpoint
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/batch-config/finalize`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          config: updatedConfig,
          finalized_prompt: finalPrompt,
          batch_mode: batchMode,
          model_name: model
        })
      });
      
      if (response.ok) {
        showToast('Configuration finalized and batch processing started!', 'success');
        setShowPromptModal(false);
        // Finalization creates a new batch config module, so upstream is no longer most recent
        setMostRecentModuleIsUpstream(false);
        navigate(`/workflow2/${clientId}/${workflowId}/batch_run`);
      } else {
        throw new Error('Failed to finalize configuration');
      }
    } catch (error) {
      console.error('Error finalizing config:', error);
      showToast('Failed to finalize configuration', 'error');
    } finally {
      setIsFinalizing(false);
    }
  };
  
  const handleCancelPrompt = () => {
    setShowPromptModal(false);
  };
  
  // Show loading spinner during initial module check or config loading
  if (isCheckingModules || isLoading) {
    return (
      <Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Review the merged topical and intent analysis carefully before batch processing all queries.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading Batch Configuration data...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Review the merged topical and intent analysis carefully before batch processing all queries.
      </Typography>
      
      {/* Show warning if upstream module is most recent OR no config exists */}
      {(mostRecentModuleIsUpstream || !configData) && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Configuration Not Ready
          </Typography>
          <Typography variant="body2">
            {mostRecentModuleIsUpstream 
              ? 'The workflow has been re-run from an earlier step. Please complete the Query Analysis and Intent Analysis steps, then confirm the merge before configuring the batch.'
              : 'No configuration found. Please complete the Query Analysis and Intent Analysis steps first.'
            }
          </Typography>
        </Alert>
      )}
      
      {/* Show configuration form only if not upstream and config exists */}
      {!mostRecentModuleIsUpstream && configData && (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            This configuration was automatically generated by merging topical and intent analysis.
          </Alert>
          
          {/* Configuration Editor */}
          <Paper 
            elevation={1}
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 3, 
              backgroundColor: alpha(theme.palette.background.paper, 0.5)
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Edit Configuration
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  - {configData.source_module1_topics} Topics & {configData.source_module3_categories} Intent Categories
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                {hasChanges && (
                  <Typography variant="caption" color="warning.main" sx={{ alignSelf: 'center', mr: 1 }}>
                    Unsaved changes
                  </Typography>
                )}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel id="sort-by-label">Sort By</InputLabel>
                  <Select
                    labelId="sort-by-label"
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => handleSortChange(e.target.value as 'name-asc' | 'name-desc' | 'weight-asc' | 'weight-desc')}
                  >
                    <MenuItem value="name-asc">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Name</span>
                        <ArrowUpIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="name-desc">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Name</span>
                        <ArrowDownIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="weight-asc">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Weight</span>
                        <ArrowUpIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </MenuItem>
                    <MenuItem value="weight-desc">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Weight</span>
                        <ArrowDownIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Tooltip title="Discard all changes and restore the original merged configuration from Topics and Intent Categories">
                  <Button
                    sx={{ py: 0 }}
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    Reset
                  </Button>
                </Tooltip>
              </Box>
            </Box>
            
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                pt: 1,
                pr: 1
              }}
            >
              <ConfigurationEditor
                categories={editedCategories}
                onChange={handleCategoriesChange}
              />
            </Box>

            {/* Validation Error Alert */}
            {validationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {validationError}
              </Alert>
            )}

            <Button
              sx={{ mt: 3 }}
              variant="contained"
              size="large"
              color="success"
              onClick={handleConfirmAndProceed}
              endIcon={isGeneratingPrompt ? <CircularProgress size={20} color="inherit" /> : <NextIcon />}
              disabled={isGeneratingPrompt}
              fullWidth
            >
              {isGeneratingPrompt ? 'Generating Prompt...' : 'Review Prompt & Proceed'}
            </Button>
          </Paper>
        </>
      )}
      
      {/* Prompt Preview Modal */}
      <PromptEditDialog
        open={showPromptModal}
        onClose={handleCancelPrompt}
        onConfirm={handleFinalizePrompt}
        initialPrompt={generatedPrompt}
        totalQueries={configData?.keyword_stats?.total_keywords}
        isProcessing={isFinalizing}
        batchMode={batchMode}
      />
    </Box>
  );
};

export default BatchConfigStep;

